// Google Cloud Professional Cloud Engineer hub configuration
import flashcards from './flashcards.js';
import practice from './practice.js';
import objective from './objective.js';

const config = {
  slug: 'google-cloud-engineer',
  vendor: 'Google Cloud',
  vendorTag: 'GCP · Cloud Engineer',
  title: 'GCP·CE',
  titleAccent: '+',
  fullTitle: 'Cloud Engineer',
  code: 'GCP-PCE',
  channelTag: 'GCPCE',
  threatPill: '● TARGET: GCP-PCE',
  themeStorageKey: 'gcpce.theme',
  storagePrefix: 'gcpce',
  passPct: 70,
  practice: { totalSeconds: 120 * 60, passPct: 70 },
  objective: { totalSeconds: 30 * 60, passPct: 70 },
  domainNames: {
    D1: 'Cloud Solution Environment',
    D2: 'Planning & Configuring',
    D3: 'Deploying & Implementing',
    D4: 'Ensuring Successful Operation',
    D5: 'Access & Security',
  },
  data: { flashcards, practice, objective },

  domains: [
    {
      id: 'D1', title: 'Setting Up a Cloud Solution Environment', weight: '17.5%', weightPct: 18,
      intro: 'Covers provisioning and configuring a GCP project from scratch — billing, IAM, APIs, CLI tools, and the organizational hierarchy that governs all resources.',
      sections: [
        {
          heading: 'GCP Organizational Hierarchy',
          items: [
            { term: 'Organization → Folders → Projects → Resources', desc: 'Resources inherit IAM policies upward; broad permissions assigned high in the tree propagate down.' },
            { term: 'Project', desc: 'The fundamental billing and API isolation unit in GCP. Every resource belongs to a project.' },
            { term: 'Folders', desc: 'Group projects by team, environment, or business unit; apply shared IAM and org policies.' },
            { term: 'Organization Node', desc: 'Root of the hierarchy backed by a Cloud Identity or Workspace domain.' },
          ],
        },
        {
          heading: 'Billing & Quotas',
          items: [
            { term: 'Billing Account', desc: 'Linked to one or more projects; controls how charges are paid. Can be set at the org level.' },
            { term: 'Budgets & Alerts', desc: 'Set budget thresholds that trigger email or Pub/Sub notifications at defined spend percentages.' },
            { term: 'Quotas', desc: 'Per-project and per-region limits on resource usage; request increases via Cloud Console or API.' },
          ],
        },
        {
          heading: 'Identity & Access Management',
          items: [
            { term: 'Principals', desc: 'Google accounts, service accounts, groups, domains, or allUsers/allAuthenticatedUsers.' },
            { term: 'Roles', desc: 'Primitive (Owner/Editor/Viewer), predefined (e.g., roles/compute.admin), or custom.' },
            { term: 'Service Accounts', desc: 'Non-human identities used by applications and VMs to authenticate to GCP APIs.' },
            { term: 'IAM Conditions', desc: 'Attribute-based conditions on bindings (e.g., time-based, resource tag-based).' },
            { term: 'Workload Identity Federation', desc: 'Allow external identities (GitHub Actions, AWS IAM) to impersonate service accounts without keys.' },
          ],
        },
        {
          heading: 'CLI & SDK Setup',
          items: [
            { term: 'gcloud CLI', desc: 'Primary tool for managing GCP resources from the command line. Configured with `gcloud init` and `gcloud config`.' },
            { term: 'Cloud Shell', desc: 'Browser-based shell with gcloud, kubectl, terraform, and other tools pre-installed at no cost.' },
            { term: 'Application Default Credentials (ADC)', desc: 'Credential resolution chain used by client libraries: env var → well-known file → metadata server.' },
          ],
        },
      ],
    },
    {
      id: 'D2', title: 'Planning and Configuring a Cloud Solution', weight: '17.5%', weightPct: 18,
      intro: 'Focuses on selecting the right compute, storage, and network services for a given workload — before provisioning anything. Requires understanding trade-offs between cost, performance, and managed-vs-DIY.',
      sections: [
        {
          heading: 'Compute Selection',
          items: [
            { term: 'Compute Engine', desc: 'IaaS VMs with full OS control. Choose machine family (general, compute, memory, accelerator) and machine type.' },
            { term: 'Google Kubernetes Engine (GKE)', desc: 'Managed Kubernetes. Standard mode gives node control; Autopilot mode is fully serverless.' },
            { term: 'Cloud Run', desc: 'Serverless containers. Scales to zero; billed per request. Best for stateless HTTP workloads.' },
            { term: 'Cloud Functions', desc: 'Lightweight event-driven functions. Triggered by HTTP, Pub/Sub, Cloud Storage, Firestore events.' },
            { term: 'App Engine', desc: 'PaaS for web apps. Standard (sandboxed, fast scaling) vs. Flexible (Docker containers, broader runtime support).' },
          ],
        },
        {
          heading: 'Storage Selection',
          items: [
            { term: 'Cloud Storage', desc: 'Object storage. Classes: Standard, Nearline (30-day), Coldline (90-day), Archive (365-day). Lifecycle rules automate transitions.' },
            { term: 'Cloud SQL', desc: 'Managed relational DB: MySQL, PostgreSQL, SQL Server. Handles backups, failover, and patching.' },
            { term: 'Cloud Spanner', desc: 'Globally distributed, strongly consistent relational DB. Use when global scale and ACID transactions are both required.' },
            { term: 'Firestore', desc: 'Serverless NoSQL document DB. Real-time sync, offline support, hierarchical data model.' },
            { term: 'Bigtable', desc: 'HBase-compatible wide-column NoSQL for massive throughput (time-series, IoT, analytics).' },
            { term: 'Persistent Disks / Filestore', desc: 'Persistent Disk = block storage for VMs; Filestore = managed NFS for shared file access.' },
          ],
        },
        {
          heading: 'Networking Fundamentals',
          items: [
            { term: 'VPC (Virtual Private Cloud)', desc: 'Global network; subnets are regional. Auto mode creates one subnet per region; custom mode gives full control.' },
            { term: 'Firewall Rules', desc: 'Stateful allow/deny rules applied to instances via network tags or service accounts.' },
            { term: 'Cloud Load Balancing', desc: 'Global (HTTP(S), SSL Proxy, TCP Proxy) or Regional (Internal/External TCP/UDP). Anycast, auto-scaling.' },
            { term: 'Cloud CDN', desc: 'Cache static content at Google edge nodes to reduce latency and origin load.' },
            { term: 'Cloud DNS', desc: 'Scalable, authoritative DNS; integrates with private zones for internal service discovery.' },
          ],
        },
        {
          heading: 'Cost Planning',
          items: [
            { term: 'Sustained Use Discounts', desc: 'Automatic discounts applied when a VM runs more than 25% of a month — no commitment needed.' },
            { term: 'Committed Use Discounts (CUDs)', desc: '1- or 3-year capacity commitments for compute and memory resources at up to 57% savings.' },
            { term: 'Preemptible / Spot VMs', desc: 'Short-lived VMs at deep discounts. May be preempted with 30-second notice. Use for fault-tolerant batch workloads.' },
            { term: 'Google Cloud Pricing Calculator', desc: 'Estimate costs before deployment; export estimate as a shareable link.' },
          ],
        },
      ],
    },
    {
      id: 'D3', title: 'Deploying and Implementing a Cloud Solution', weight: '25%', weightPct: 25,
      intro: 'The largest domain — covers how to actually build and deploy infrastructure using Compute Engine, GKE, serverless, storage, and CI/CD pipelines. Emphasis on Infrastructure as Code and automation.',
      sections: [
        {
          heading: 'Compute Engine Deployment',
          items: [
            { term: 'Instance Templates', desc: 'Reusable VM configuration (machine type, image, disk, metadata) used by Managed Instance Groups.' },
            { term: 'Managed Instance Groups (MIGs)', desc: 'Autoscaling groups of identical VMs with autohealing. Regional MIGs span zones for HA.' },
            { term: 'Startup Scripts', desc: 'Shell scripts run at VM boot to install packages, configure the app, and register with services.' },
            { term: 'Custom Images & Snapshots', desc: 'Capture OS state as a custom image for fast, repeatable provisioning; snapshots back up persistent disks.' },
          ],
        },
        {
          heading: 'GKE & Containers',
          items: [
            { term: 'Kubernetes Workloads', desc: 'Deployment (stateless), StatefulSet (ordered, persistent), DaemonSet (one per node), Job/CronJob (batch).' },
            { term: 'Services & Ingress', desc: 'ClusterIP (internal), NodePort, LoadBalancer expose Pods. Ingress routes HTTP(S) to Services.' },
            { term: 'Horizontal Pod Autoscaler (HPA)', desc: 'Scales Pod replicas based on CPU/memory metrics or custom metrics from Cloud Monitoring.' },
            { term: 'Workload Identity', desc: 'Binds a Kubernetes ServiceAccount to a GCP service account — no key files needed in containers.' },
            { term: 'Artifact Registry', desc: 'Private Docker and package registry; replaces Container Registry. Integrated with Cloud Build.' },
          ],
        },
        {
          heading: 'Infrastructure as Code & CI/CD',
          items: [
            { term: 'Terraform on GCP', desc: 'Declare GCP resources in HCL; `terraform plan` previews, `apply` provisions. State stored in GCS backend.' },
            { term: 'Cloud Deployment Manager', desc: 'GCP-native IaC using YAML/Jinja/Python templates. Less popular than Terraform but fully integrated.' },
            { term: 'Cloud Build', desc: 'Managed CI/CD pipeline. Triggered by GitHub/GitLab pushes; runs build steps in containers.' },
            { term: 'Cloud Deploy', desc: 'Managed continuous delivery to GKE, Cloud Run, and GCE with progressive delivery and approval gates.' },
            { term: 'Binary Authorization', desc: 'Enforce policy that only signed, trusted container images can be deployed to GKE.' },
          ],
        },
        {
          heading: 'Data & Storage Deployment',
          items: [
            { term: 'Cloud Storage Buckets', desc: 'Create with gsutil or gcloud; configure ACLs, versioning, lifecycle rules, and uniform bucket-level access.' },
            { term: 'Cloud SQL High Availability', desc: 'Regional HA with automatic failover to standby instance. Point-in-time recovery for PITR.' },
            { term: 'Pub/Sub', desc: 'Asynchronous messaging. Publishers send to topics; subscribers pull or receive push deliveries.' },
            { term: 'Dataflow', desc: 'Managed Apache Beam for batch and streaming ETL. Autoscaling, exactly-once processing.' },
          ],
        },
      ],
    },
    {
      id: 'D4', title: 'Ensuring Successful Operation of a Cloud Solution', weight: '22.5%', weightPct: 22,
      intro: 'Covers the day-2 operations discipline: monitoring, logging, alerting, scaling, incident response, and keeping a running cloud solution healthy and cost-efficient.',
      sections: [
        {
          heading: 'Cloud Monitoring & Observability',
          items: [
            { term: 'Cloud Monitoring (formerly Stackdriver)', desc: 'Collects metrics from GCP resources, custom metrics, Prometheus exporters, and external systems.' },
            { term: 'Metrics Explorer', desc: 'Ad-hoc charting of any metric in the project using MQL or the point-and-click interface.' },
            { term: 'Dashboards', desc: 'Pin metric charts, log panels, and SLO status into shareable dashboards.' },
            { term: 'Alerting Policies', desc: 'Define conditions on metrics; notify via email, PagerDuty, Slack, or Pub/Sub.' },
            { term: 'Uptime Checks', desc: 'Synthetic HTTP/TCP checks from multiple global locations; alerts on failure.' },
          ],
        },
        {
          heading: 'Cloud Logging',
          items: [
            { term: 'Log Types', desc: 'Admin Activity, Data Access, System Event, Policy Denied, and user-defined application logs.' },
            { term: 'Log Router / Log Sinks', desc: 'Forward logs to Cloud Storage, BigQuery, or Pub/Sub for long-term retention or analysis.' },
            { term: 'Log-based Metrics', desc: 'Convert log entry counts or field values into Cloud Monitoring metrics for alerting.' },
            { term: 'Cloud Logging Retention', desc: 'Admin Activity logs: 400 days; Data Access and user logs: 30 days (configurable).' },
          ],
        },
        {
          heading: 'Cloud Trace & Profiler',
          items: [
            { term: 'Cloud Trace', desc: 'Distributed tracing collects latency data for HTTP requests across App Engine, GKE, Cloud Run.' },
            { term: 'Cloud Profiler', desc: 'Continuous CPU and heap profiling for running applications. Zero-overhead sampling.' },
            { term: 'Error Reporting', desc: 'Aggregates application exceptions and surfaces the most frequent errors with stack traces.' },
          ],
        },
        {
          heading: 'Scaling & Reliability',
          items: [
            { term: 'MIG Autoscaling', desc: 'Scale Compute Engine MIGs on CPU utilization, HTTP load balancing utilization, or custom metrics.' },
            { term: 'GKE Cluster Autoscaler', desc: 'Adds/removes nodes based on pending Pod resource requests.' },
            { term: 'Cloud Run Autoscaling', desc: 'Scales container instances 0 → N based on concurrent requests; concurrency limit controls scaling rate.' },
            { term: 'Health Checks', desc: 'HTTP/TCP/gRPC checks used by load balancers and MIGs to route traffic away from unhealthy backends.' },
            { term: 'Cloud Armor', desc: 'DDoS protection and WAF rules applied at the load balancer layer.' },
          ],
        },
      ],
    },
    {
      id: 'D5', title: 'Configuring Access and Security', weight: '17.5%', weightPct: 18,
      intro: 'Focuses on securing GCP resources through IAM, network controls, encryption, key management, and security posture tools like Security Command Center.',
      sections: [
        {
          heading: 'IAM Advanced Patterns',
          items: [
            { term: 'Resource Hierarchy Policy Inheritance', desc: 'IAM policies are inherited from parent to child; deny policies block even inherited allows.' },
            { term: 'Least Privilege & Separation of Duties', desc: 'Use predefined roles; never assign primitive Owner/Editor in production; use custom roles for fine-grained control.' },
            { term: 'Service Account Best Practices', desc: 'One SA per application; avoid attaching broad roles; prefer Workload Identity over key files.' },
            { term: 'IAM Recommender', desc: 'ML-based tool that suggests role downgrades based on actual permission usage in the last 90 days.' },
          ],
        },
        {
          heading: 'Network Security',
          items: [
            { term: 'VPC Service Controls', desc: 'Create a security perimeter around GCP services to prevent data exfiltration even from authorized users.' },
            { term: 'Private Google Access', desc: 'Allow VMs without external IPs to reach Google APIs via the internal network.' },
            { term: 'Cloud NAT', desc: 'Provides outbound internet for VMs without external IPs; no inbound connections allowed.' },
            { term: 'Shared VPC', desc: 'Central VPC network shared across multiple projects; networking owned by host project, workloads in service projects.' },
            { term: 'VPC Peering', desc: 'Connect two VPC networks privately; traffic stays on Google network. Not transitive.' },
          ],
        },
        {
          heading: 'Encryption & Key Management',
          items: [
            { term: 'Default Encryption', desc: 'All GCP data at rest is encrypted by default using Google-managed keys.' },
            { term: 'Customer-Managed Encryption Keys (CMEK)', desc: 'Supply your own keys stored in Cloud KMS; GCP encrypts data with your key. You control key lifecycle.' },
            { term: 'Customer-Supplied Encryption Keys (CSEK)', desc: 'Provide the raw key directly to the API on every request; GCP never stores the key.' },
            { term: 'Cloud KMS', desc: 'Managed key management service. Create, rotate, disable, and destroy symmetric and asymmetric keys.' },
            { term: 'Secret Manager', desc: 'Store, access, and audit API keys, passwords, and certificates with version history.' },
          ],
        },
        {
          heading: 'Security Posture & Compliance',
          items: [
            { term: 'Security Command Center (SCC)', desc: 'Centralized security and risk platform. Asset inventory, vulnerability findings, threat detection.' },
            { term: 'Cloud Audit Logs', desc: 'Admin Activity (default on), Data Access (opt-in), System Event, Policy Denied logs. Immutable for 400 days.' },
            { term: 'Policy Intelligence Tools', desc: 'Policy Analyzer, IAM Recommender, and Policy Troubleshooter help diagnose and right-size permissions.' },
            { term: 'Organization Policy Service', desc: 'Enforce guardrails across the entire org — restrict VM images, prevent external IPs, require CMEK.' },
          ],
        },
      ],
    },
  ],

  weightingMap: [
    { id: 'D1', weight: '17.5%', title: 'Setup Environment', desc: 'Project hierarchy, IAM, billing, CLI. ~2 study weeks.' },
    { id: 'D2', weight: '17.5%', title: 'Plan & Configure', desc: 'Compute, storage, network selection, cost planning. ~2 study weeks.' },
    { id: 'D3', weight: '25%', title: 'Deploy & Implement', desc: 'Largest domain. GKE, serverless, IaC, CI/CD. ~3 study weeks.' },
    { id: 'D4', weight: '22.5%', title: 'Ensure Successful Operation', desc: 'Monitoring, logging, scaling, reliability. ~2.5 study weeks.' },
    { id: 'D5', weight: '17.5%', title: 'Access & Security', desc: 'IAM, network security, encryption, SCC. ~2 study weeks.' },
    { id: 'FINAL', weight: '100%', title: 'Mock + Review', desc: 'Practice exams + cheat sheet. Final sprint.' },
  ],
};

export default config;
